import React from 'react';
import Table from '../../components/Table/Table';
import InventoryCategoryForm from '../../components/Form/InventoryCategoryForm';
import { Product, InventoryCategory } from '../../types';

interface CategoriesTabProps {
  products: Product[];
  categories: InventoryCategory[];
  filteredCategories: InventoryCategory[];
  showCategoryForm: boolean;
  setShowCategoryForm: (show: boolean) => void;
  editingCategory: InventoryCategory | undefined;
  setEditingCategory: (category: InventoryCategory | undefined) => void;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  categoryStatusFilter: string;
  setCategoryStatusFilter: (value: string) => void;
  onAddCategory: (data: Omit<InventoryCategory, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onEditCategory: (data: Omit<InventoryCategory, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onDeleteCategory: (id: string) => void;
  onEditCategoryClick: (category: InventoryCategory) => void;
  onExport: () => void;
  onPrint: () => void;
  onClearFilters: () => void;
}

const CategoriesTab: React.FC<CategoriesTabProps> = ({
  products, categories, filteredCategories,
  showCategoryForm, setShowCategoryForm,
  editingCategory, setEditingCategory,
  searchTerm, setSearchTerm,
  showFilters, setShowFilters,
  categoryStatusFilter, setCategoryStatusFilter,
  onAddCategory, onEditCategory, onDeleteCategory, onEditCategoryClick,
  onExport, onPrint, onClearFilters,
}) => {
  const categoryColumns = [
    {
      key: 'name',
      label: 'Category Name',
      sortable: true,
      render: (value: string) => <span className="font-semibold text-gray-900">{value}</span>,
    },
    { key: 'description', label: 'Description', sortable: true },
    {
      key: 'productCount',
      label: 'Products',
      render: (_: any, item: InventoryCategory) => {
        const count = products.filter(p => p.categoryId === item.id).length;
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {count} products
          </span>
        );
      },
    },
    {
      key: 'isActive',
      label: 'Status',
      render: (value: boolean) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_: any, item: InventoryCategory) => (
        <div className="flex space-x-2">
          <button onClick={() => onEditCategoryClick(item)} className="text-blue-600 hover:text-blue-900 font-medium">Edit</button>
          <button onClick={() => onDeleteCategory(item.id)} className="text-red-600 hover:text-red-900 font-medium">Delete</button>
        </div>
      ),
    },
  ];

  if (showCategoryForm) {
    return (
      <div className="card p-6">
        <h2 className="text-xl font-semibold mb-4">
          {editingCategory ? 'Edit Category' : 'Add New Category'}
        </h2>
        <InventoryCategoryForm
          onSubmit={editingCategory ? onEditCategory : onAddCategory}
          initialData={editingCategory}
          onCancel={() => { setShowCategoryForm(false); setEditingCategory(undefined); }}
        />
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-4">
          <div className="text-sm text-gray-600">Total Categories</div>
          <div className="text-2xl font-bold text-gray-900">{categories.length}</div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-gray-600">Active Categories</div>
          <div className="text-2xl font-bold text-green-600">{categories.filter(c => c.isActive).length}</div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-gray-600">Empty Categories</div>
          <div className="text-2xl font-bold text-gray-600">{categories.filter(c => !products.some(p => p.categoryId === c.id)).length}</div>
        </div>
      </div>

      <div className="card p-4 space-y-4">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <input
            type="text"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full lg:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
          />
          <div className="flex flex-wrap gap-2 w-full lg:w-auto">
            <button onClick={() => setShowFilters(!showFilters)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-ocean-500">
              <svg className="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filters
            </button>
            <button onClick={onExport} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-ocean-500">
              <svg className="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export
            </button>
            <button onClick={onPrint} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-ocean-500">
              <svg className="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print
            </button>
            <button onClick={() => setShowCategoryForm(true)} className="px-4 py-2 bg-ocean-600 text-white rounded-lg hover:bg-ocean-700 focus:outline-none focus:ring-2 focus:ring-ocean-500">
              + Add Category
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={categoryStatusFilter} onChange={(e) => setCategoryStatusFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent">
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="flex items-end">
              <button onClick={onClearFilters} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg">
                Clear All Filters
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="card overflow-hidden">
        <Table data={filteredCategories} columns={categoryColumns} />
      </div>
    </>
  );
};

export default CategoriesTab;
